<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { TimepickerUI } from 'timepicker-ui'
import 'timepicker-ui/main.css'

const input = ref<HTMLInputElement | null>(null)
let picker: TimepickerUI | null = null

const props = defineProps<{
  modelValue?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

function formatConfirmValue(data: { hour?: string; minutes?: string; type?: string }) {
  return `${data.hour}:${data.minutes} ${data.type}`
}

onMounted(() => {
  if (!input.value) return

  // Current time
  const now = new Date()

  let hours = now.getHours()
  const minutes = now.getMinutes()

  const type = hours >= 12 ? 'PM' : 'AM'

  hours = hours % 12
  hours = hours === 0 ? 12 : hours

  const defaultTime = `${hours}:${minutes.toString().padStart(2, '0')} ${type}`

  // Set initial input value
  input.value.value = defaultTime

  // Emit initial value if using v-model
  emit('update:modelValue', defaultTime)

  picker = new TimepickerUI(input.value, {
    clock: {
      type: '12h',
      incrementMinutes: 5,
    },
    ui: {
      mode: 'clock',
      // mobile: true keeps the dial collapsed until hour/minute is tapped.
      // false shows the analog clock immediately (Material-style picker).
      mobile: false,
      editable: false,
      animation: true,
      theme: 'basic',
    },
    callbacks: {
      onConfirm: (data) => {
        emit('update:modelValue', formatConfirmValue(data))
      },
    },
  })

  picker.create()

  if (props.modelValue) {
    picker.setValue(props.modelValue)
  }
})

onUnmounted(() => {
  picker?.destroy()
  picker = null
})
</script>

<template>
  <input
    ref="input"
    :value="modelValue ?? ''"
    type="text"
    inputmode="none"
    readonly
    class="w-full rounded-xl border border-gray-300 pl-10 px-4 py-3 text-sm focus:border-violet-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
    placeholder="Select time"
  />
</template>
